// # User Dashboard Component
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../../auth/firebase';
import './user_dashboard.css';
import { useAuthStore } from '../../auth/state';
import DashboardFooter from '../../components/DashboardFooter';
import { Lock } from '@mui/icons-material';
import sessionManager from '../../auth/session';
import DashboardHeader from '../../components/DashboardHeader';
import UserDashboardUX from '../../components/ux_extra/main_pages/usr_dashboard_ux';
// # Import verification modal
import VerificationModal from '../../auth/verify/verif_modal';
import { doc, updateDoc } from 'firebase/firestore';  // # Import for updating theme
import cacheManager from '../../auth/cache_manager';  // # Import cache manager

// # Helper function to get display name - moved outside component
const getDisplayName = (userProfile, user) => {
  let name = '';
  let username = '';

  // Get name
  if (userProfile?.lastName) {
    name = `${userProfile.lastName}`;
  } else if (userProfile?.firstName) {
    name = `${userProfile.firstName}`;
  } else {
    name = `${user?.email?.split('@')[0] || 'User'}`;
  }

  // Get username
  if (userProfile?.username) {
    username = `@${userProfile.username}`;
  }

  return { name, username };
};

// # Helper function to get profile photo URL - moved outside component
const getProfilePhotoUrl = (userProfile) => {
  try {
    if (!userProfile?.photoURL) return '/icons/pfp.png';
    if (userProfile.photoURL === '') return '/icons/pfp.png';
    
    // Check if it's a full URL (from Storage)
    if (userProfile.photoURL.startsWith('http')) {
      return userProfile.photoURL;
    }
    
    // If it's just a filename, assume it's the default
    return '/icons/pfp.png';
  } catch (error) {
    console.error('Error getting profile photo:', error);
    return '/icons/pfp.png';
  }
};

function UserDashboard() {
  const navigate = useNavigate();
  const { user, userProfile, userDashboard, emailVerified } = useAuthStore();
  // # Add state for showing verification modal
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);  // # Add dark mode state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Check if we should show verification reminder
  useEffect(() => {
    if (user && !emailVerified) {
      // # Check if reminder is suppressed
      const verificationReminder = localStorage.getItem('verificationReminder');
      
      if (verificationReminder) {
        const reminderData = JSON.parse(verificationReminder);
        const now = Date.now();
        
        // # If reminder time has passed, show modal
        if (now > reminderData.nextReminder) {
          setShowVerificationModal(true);
          // # Clear old reminder
          localStorage.removeItem('verificationReminder');
        }
      } else {
        // # No reminder set, show modal
        setShowVerificationModal(true);
      }
    }
  }, [user, emailVerified]);

  // # Memoize subscription checks to prevent recalculation and use cached data
  const subscriptionStatus = React.useMemo(() => {
    // # Get current user ID
    const uid = user?.uid;
    if (!uid) return {
      isSubscribedToStep1: false,
      isSubscribedToStep2: false,
      isSubscribedToStep1Mentorship: false,
      isSubscribedToStep2Mentorship: false,
      hasMentorship: false
    };

    // # Check direct subscriptions first
    const hasStep1Library = sessionManager.isSubscribed('step1_library');
    const hasStep2Library = sessionManager.isSubscribed('step2_library');
    const hasStep1Mentorship = sessionManager.isSubscribed('step1_mentorship');
    const hasStep2Mentorship = sessionManager.isSubscribed('step2_mentorship');

    // # Apply access rules: mentorship subscriptions also grant access to corresponding libraries
    const isSubscribedToStep1 = hasStep1Library || hasStep1Mentorship;
    const isSubscribedToStep2 = hasStep2Library || hasStep2Mentorship;
    
    return {
      isSubscribedToStep1,
      isSubscribedToStep2,
      isSubscribedToStep1Mentorship: hasStep1Mentorship,
      isSubscribedToStep2Mentorship: hasStep2Mentorship,
      hasMentorship: hasStep1Mentorship || hasStep2Mentorship
    };
  }, [userProfile?.subscriptions, user?.uid]); // Only recompute when subscriptions or user ID change

  // # Memoize display name to prevent unnecessary recalculations
  const displayName = React.useMemo(() => 
    getDisplayName(userProfile, user), [userProfile, user]);

  // # Update session manager's subscription data when userProfile changes
  React.useEffect(() => {
    if (userProfile?.subscriptions && sessionManager.getSession()) {
      // Update session with latest subscription data
      sessionManager.getSession().subscriptions = userProfile.subscriptions;
    }
  }, [userProfile?.subscriptions]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Group dashboard items into sections
  const dashboardSections = {
    study: {
      title: 'USMLE',
      items: [
        {
          title: 'Step 1 Library',
          icon: '/icons/dash/step_1.png',
          description: subscriptionStatus.isSubscribedToStep1 
            ? 'Access Step 1 study materials and resources'
            : 'Subscribe to access Step 1 materials',
          path: '/library/step1',
          locked: !subscriptionStatus.isSubscribedToStep1
        },
        {
          title: 'Step 2 Library',
          icon: '/icons/dash/step_2.png',
          description: subscriptionStatus.isSubscribedToStep2 
            ? 'Access Step 2 study materials and resources'
            : 'Subscribe to access Step 2 materials',
          path: '/library/step2',
          locked: !subscriptionStatus.isSubscribedToStep2
        },
        {
          title: 'Step 1 Mentorship',
          icon: '/icons/dash/one.png',
          description: subscriptionStatus.isSubscribedToStep1Mentorship
            ? 'Access Step 1 mentorship program'
            : 'Subscribe to access Step 1 mentorship',
          path: '/mentorship/step1',
          locked: !subscriptionStatus.isSubscribedToStep1Mentorship
        },
        {
          title: 'Step 2 Mentorship',
          icon: '/icons/dash/two.png',
          description: subscriptionStatus.isSubscribedToStep2Mentorship
            ? 'Access Step 2 mentorship program'
            : 'Subscribe to access Step 2 mentorship',
          path: '/mentorship/step2',
          locked: !subscriptionStatus.isSubscribedToStep2Mentorship
        }
      ]
    },
    personal: {
      title: 'Personal Tools',
      items: [
        {
          title: 'My Bookmarks',
          icon: '/icons/dash/bookmark.png',
          description: 'Manage your saved items and resources',
          path: '/bookmarks'
        },
        {
          title: 'My Notes',
          icon: '/icons/dash/notes.jpg',
          description: 'Create and manage your study notes',
          path: '/notes'
        },
        {
          title: 'My Flashcards',
          icon: '/icons/dash/flashcards.png',
          description: 'Create and study with flashcards',
          path: '/flashcards',
          locked: false
        },
        {
          title: 'Labs',
          icon: '/icons/dash/labs.png',
          description: 'Reference ranges for Blood, Serum, CSF, and Urine',
          path: '/labs'
        }
      ]
    },
    community: {
      title: 'TEMP-U Community',
      items: [
        {
          title: 'Taking Notes',
          icon: '/icons/dash/external_notes.png',
          description: 'Create and manage your study notes',
          path: '/community/notes'
        },
        {
          title: 'Guide',
          icon: '/icons/dash/guide.png',
          description: 'Comprehensive USMLE study guides',
          path: '/community/guide'
        },
        {
          title: 'Take a Break',
          icon: '/icons/dash/take_break.png',
          description: 'Relax and recharge 😴',
          path: '/community/break'
        },
        {
          title: 'Events',
          icon: '/icons/dash/events.png',
          description: 'Virtual meetings, lives and podcasts',
          path: '/community/events'
        },
        {
          title: 'Roadmaps',
          icon: '/icons/dash/roadmap.png',
          description: 'Study guides and learning paths',
          path: '/community/roadmaps'
        },
        {
          title: 'Content Updates',
          icon: '/icons/dash/updates.png',
          description: 'Latest platform updates and new content',
          path: '/community/updates'
        },
        {
          title: 'Clinical Rotations',
          icon: '/icons/dash/med_student.png',
          description: 'Clinical rotation resources and agencies',
          path: '/community/rotations'
        }
      ]
    },
    blog: {
      title: 'Social',
      items: [
        {
          title: 'Articles',
          icon: '/icons/dash/article.png',
          description: 'Read our latest articles',
          path: '/blog/articles'
        },
        {
          title: 'Q & A',
          icon: '/icons/dash/qa.png',
          description: 'Browse questions and answers',
          //path: '/blog/qa'
        },
        {
          title: 'Experiences',
          icon: '/icons/dash/experiences.png',
          description: 'Share and learn from other students',
          path: '/community/experiences'
        },
        {
          title: 'Discussions',
          icon: '/icons/dash/discussion.png',
          description: 'Join community discussions',
          path: '/blog/discussions'
        }
      ]
    },
    extras: {
      title: 'TEMP-U Extras',
      items: [
        {
          title: 'Gallery',
          icon: '/icons/dash/gallery.png',
          description: 'Visual learning resources',
          path: '/extras/gallery'
        },
        {
          title: 'Clinical Library',
          icon: '/icons/dash/clinical.png',
          description: 'Clinical skills resources',
          path: '/extras/clinical'
        },
        {
          title: 'Research Library',
          icon: '/icons/dash/laboratory.png',
          description: 'Research papers and publications',
          path: '/extras/research'
        },
        {
          title: 'OET',
          icon: '/icons/dash/oet.png',
          description: 'OET preparation materials',
          path: '/extras/oet'
        }
      ]
    },
    
    support: {
      title: 'Support & Settings',
      items: [
        {
          title: 'Settings',
          icon: '/icons/dash/settings.png',
          description: 'Manage your account settings and preferences',
          path: '/settings'
        },
        {
          title: 'Support',
          icon: '/icons/dash/support.png',
          description: 'Get help and support for any issues',
          path: '/support'
        },
        {
          title: 'Feedback',
          icon: '/icons/dash/feedback.jpg',
          description: 'Share your thoughts and suggestions',
          path: '/feedback'
        },
        {
          title: 'Reviews',
          icon: '/icons/dash/reviews.png',
          description: 'Read success stories from our students',
          path: '/reviews'
        }
      ]
    }
  };

  // # Handle sign out
  const handleSignOut = async () => {
    try {
      const { signOut } = useAuthStore.getState(); // Get signOut from store
      await signOut(); // Use the store's signOut method
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    }
  };

  // # Apply stagger animation to sections
  useEffect(() => {
    const sections = document.querySelectorAll('.usr_dsh_section');
    sections.forEach((section, index) => {
      section.style.animationDelay = `${0.2 * index}s`;
    });
  }, []);

  // # Handle item click with lock check
  const handleItemClick = (item) => {
    if (item.locked) {
      navigate('/products');
    } else {
      navigate(item.path);
    }
  };

  // # Close verification modal
  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
  };

  return (
    <div className={`usr_dsh_dashboard-container ${isDarkMode ? 'usr_dsh_dark-mode' : ''}`}>
      {/* # Pass theme props to header */}
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />

      {/* Cover Image */}
      <div className="usr_dsh_cover-section">
        <img 
          src="/icons/cover_ocean.jpg" 
          alt="Cover" 
          className="usr_dsh_cover-image"
        />
      </div>

      {/* Profile Section */}
      <div className="usr_dsh_profile-section">
        <div 
          className="usr_dsh_profile-image-container"
          onClick={() => navigate('/profile')}
        >
          <img 
            src={getProfilePhotoUrl(userProfile)}
            alt="Profile" 
            className="usr_dsh_profile-image"
          />
        </div>
        <div className="usr_dsh_profile-info">
          <h2 className="usr_dsh_profile-name">
            {displayName.name}
          </h2>
          <div className="usr_dsh-user-username">
            {displayName.username}
          </div>
        </div>
      </div>

      {/* Enhanced Dashboard Sections with Carousel */}
      <UserDashboardUX sections={dashboardSections} isDarkMode={isDarkMode} />

      {/* Verification Modal */}
      {showVerificationModal && !emailVerified && (
        <VerificationModal onClose={handleCloseVerificationModal} />
      )}

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default UserDashboard;
